import { useAiSummary, useGenerateAiSummary } from '../../api/useAiSummary';
import { extractString } from '../../utils/sanitizeData';

interface AiExecutiveSummaryProps {
  publicToken?: string;
  summary?: unknown;
  keyInsights?: unknown[];
  isLoading?: boolean;
}

export default function AiExecutiveSummary({
  publicToken,
  summary: fallbackSummary,
  keyInsights = [],
  isLoading: propLoading = false,
}: AiExecutiveSummaryProps) {
  const { data: aiData, isLoading: queryLoading } = useAiSummary(publicToken);
  const generateSummaryMutation = useGenerateAiSummary();

  const handleRegenerate = () => {
    if (publicToken) {
      generateSummaryMutation.mutate({ publicToken, force: true });
    }
  };

  const isGenerating = generateSummaryMutation.isPending;
  const isLoading = propLoading || queryLoading || isGenerating;
  const rawSummary = aiData?.summary || fallbackSummary;
  const activeSummary = extractString(rawSummary, 'No summary narrative available.');

  // Cleanly format entity highlights from HTML (such as <a class="...">...</a> or <b>...</b>)
  const formattedSummary = activeSummary
    .replace(/<a\b[^>]*>(.*?)<\/a>/gi, '<strong class="font-semibold text-brand-navy dark:text-white">$1</strong>')
    .replace(/<b\b[^>]*>(.*?)<\/b>/gi, '<strong class="font-semibold text-brand-navy dark:text-white">$1</strong>')
    .replace(/<strong\b[^>]*>(.*?)<\/strong>/gi, '<strong class="font-semibold text-brand-navy dark:text-white">$1</strong>')
    .replace(/<span\b[^>]*>(.*?)<\/span>/gi, '<strong class="font-semibold text-brand-navy dark:text-white">$1</strong>');

  const safeInsights = (keyInsights && Array.isArray(keyInsights) ? keyInsights : [])
    .map((ins) => extractString(ins, ''))
    .filter(Boolean);

  return (
    <div className="bg-white dark:bg-boxdark border border-brand-green/35 dark:border-brand-green/20 rounded-2.5xl p-5 sm:p-6 shadow-card mb-6 flex flex-col gap-3">
      {/* Title lockup */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#12b98a] to-[#16EA9E] flex items-center justify-center text-brand-navy shadow-sm">
            <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
              <path d="M12 2a2 2 0 012 2c0 .74-.4 1.38-1 1.72V7h4a3 3 0 013 3v8a3 3 0 01-3 3H7a3 3 0 01-3-3v-8a3 3 0 013-3h4V5.72A2 2 0 0110 4a2 2 0 012-2zm-4 8a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm8 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm-6 6h4a1 1 0 100-2h-4a1 1 0 100 2z" />
            </svg>
          </div>
          <h2 className="text-xs font-mono font-bold tracking-wider text-[#0e9c6c] dark:text-brand-green uppercase">
            AI Executive Summary
          </h2>
        </div>

        <div className="flex items-center gap-3">
          {publicToken && (
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={isLoading}
              className="text-[11px] font-mono font-semibold text-brand-vib hover:text-brand-vib-hover dark:text-brand-light flex items-center gap-1 transition-colors cursor-pointer"
            >
              <svg className={`w-3.5 h-3.5 fill-current ${isGenerating ? 'animate-spin' : ''}`} viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              {isGenerating ? 'Regenerating…' : 'Regenerate'}
            </button>
          )}
          <span className="text-[10px] font-mono text-brand-soft dark:text-bodydark2">
            Model: InvoiceParse v2.0
          </span>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-3 py-4 text-sm text-brand-muted dark:text-bodydark">
          <div className="w-5 h-5 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
          <span>Generating executive analysis and risk scan…</span>
        </div>
      ) : (
        <>
          <p
            className="text-sm text-[#3b4569] dark:text-bodydark1 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: formattedSummary }}
          />

          {/* Key Insights List - only render if real API insights exist */}
          {safeInsights.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 pt-3 border-t border-brand-line/60 dark:border-strokedark">
              {safeInsights.map((insight, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2 p-2.5 rounded-xl bg-brand-green-tint/60 dark:bg-emerald-950/30 text-xs text-[#0a6b4a] dark:text-emerald-300 border border-brand-green/20 dark:border-emerald-800"
                >
                  <span className="font-bold text-brand-green">✓</span>
                  <span className="leading-snug">{insight}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
